/**
 * ManuscriptBulkToolsDialog.jsx
 *
 * Writer-facing dialog surfaced from the File menu.
 * Two sections:
 *   1. Export All  — preview project list, then download a single ZIP
 *                    of DOCX files with full metadata appendix.
 *   2. Import      — upload a ZIP (or individual .docx/.txt/.pdf) and
 *                    create new projects auto-split by chapter headings.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Download, Upload, FileArchive, BookOpen, Loader2, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { userManuscriptApi } from '@/lib/api';
import ExtractionReviewPanel from '@/components/panels/ExtractionReviewPanel';

// ── helpers ──────────────────────────────────────────────────────────────────
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function today() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

// ── Project preview list ──────────────────────────────────────────────────────
function ProjectPreviewList({ projects }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? projects : projects.slice(0, 5);
  return (
    <div className="rounded-md border border-border bg-muted/30 text-[12px]">
      {visible.map((p) => (
        <div key={p.project_id} className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-1.5 last:border-b-0">
          <span className="truncate font-medium text-foreground">
            {p.resolved_title || p.raw_title || 'Untitled'}
          </span>
          <span className="shrink-0 text-muted-foreground">{p.chapter_count ?? 0} ch</span>
          {p.is_hash_title && (
            <Badge variant="outline" className="shrink-0 text-[9.5px] border-amber-400/50 text-amber-700 bg-amber-500/10">
              title resolved
            </Badge>
          )}
        </div>
      ))}
      {projects.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 py-1.5 text-[11px] text-primary hover:bg-muted/60 transition-colors"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? 'Show less' : `Show ${projects.length - 5} more`}
        </button>
      )}
    </div>
  );
}

// ── Import result summary ─────────────────────────────────────────────────────
function ImportResult({ result }) {
  if (!result) return null;
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-[12px]">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
        <span className="font-semibold text-foreground">
          {result.projects_created} project{result.projects_created !== 1 ? 's' : ''} created
        </span>
        <span className="text-muted-foreground">from {result.files_processed} file{result.files_processed !== 1 ? 's' : ''}</span>
      </div>
      {result.created?.map((c) => (
        <div key={c.project_id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1 last:border-b-0 pl-6">
          <span className="truncate font-medium">{c.title}</span>
          <span className="shrink-0 text-muted-foreground">{c.chapter_count} ch · {(c.total_words || 0).toLocaleString()} words</span>
        </div>
      ))}
      {result.errors?.length > 0 && (
        <div className="mt-2 space-y-1">
          {result.errors.map((e, i) => (
            <div key={i} className="flex items-start gap-1.5 text-amber-700">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="break-all">{e.file}: {e.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────
export default function ManuscriptBulkToolsDialog({ open: isOpen, onOpenChange }) {
  // Export state
  const [previewData,    setPreviewData]    = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading,    setDownloading]    = useState(false);

  // Import state
  const [importFile,    setImportFile]    = useState(null);
  const [importing,     setImporting]     = useState(false);
  const [importResult,  setImportResult]  = useState(null);
  const [dragOver,      setDragOver]      = useState(false);
  // Extraction review — show panel for the first created project
  const [reviewProjectId, setReviewProjectId] = useState(null);
  const fileInputRef = useRef(null);

  // ── Export handlers ─────────────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const data = await userManuscriptApi.exportPreview();
      setPreviewData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load project list.');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const downloadZip = useCallback(async () => {
    setDownloading(true);
    const tid = toast.loading('Building your manuscript archive…');
    try {
      const res  = await userManuscriptApi.exportDownload();
      const blob = res.data;
      triggerBlobDownload(blob, `manuscripts_${today()}.zip`);
      toast.success(`ZIP downloaded — ${previewData?.project_count ?? ''} manuscripts`, { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed.', { id: tid });
    } finally {
      setDownloading(false);
    }
  }, [previewData]);

  // ── Import handlers ─────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setImportFile(f); setImportResult(null); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setImportFile(f); setImportResult(null); }
  };

  const runImport = useCallback(async () => {
    if (!importFile) return;
    setImporting(true);
    const tid = toast.loading('Parsing and importing manuscripts…');
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const result = await userManuscriptApi.import(fd);
      setImportResult(result);
      if (result.projects_created > 0) {
        toast.success(`Imported ${result.projects_created} project${result.projects_created !== 1 ? 's' : ''}`, { id: tid });
        // Auto-open review panel for first created project
        if (result.created?.[0]?.project_id) {
          setReviewProjectId(result.created[0].project_id);
        }
        // Reload preview so the count updates
        setPreviewData(null);
      } else {
        toast.warning('No projects were created — check the file format.', { id: tid });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed.', { id: tid });
    } finally {
      setImporting(false);
    }
  }, [importFile]);

  const ACCEPT = '.zip,.docx,.txt,.pdf';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl gap-0 overflow-hidden p-0"
        data-testid="manuscript-bulk-tools-dialog"
      >
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileArchive className="h-4.5 w-4.5 text-primary" />
            Bulk Manuscript Tools
          </DialogTitle>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Export all your manuscripts for offline editing, or import a batch of files as new projects.
          </p>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-6">

          {/* ── EXPORT SECTION ─────────────────────────────────────────── */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-[13px] font-semibold">
                <Download className="h-3.5 w-3.5 text-primary" />
                Export all manuscripts
              </h3>
              {!previewData && (
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-[11.5px]"
                  onClick={loadPreview}
                  disabled={previewLoading}
                  data-testid="bulk-export-preview-btn"
                >
                  {previewLoading
                    ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Loading…</>
                    : 'Preview projects'}
                </Button>
              )}
            </div>

            <p className="text-[11.5px] text-muted-foreground leading-relaxed">
              Downloads a single ZIP file containing one Word document (.docx) per project.
              Each file includes the full manuscript text plus a complete metadata appendix —
              character bible, world bible, codex entries, notebook notes, continuity findings,
              edit walker passes, voice audit and readability scores.
            </p>

            {previewData && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11.5px] text-muted-foreground">
                  <span>{previewData.project_count} project{previewData.project_count !== 1 ? 's' : ''} found</span>
                  <button
                    type="button"
                    onClick={() => setPreviewData(null)}
                    className="text-[10.5px] underline hover:no-underline"
                  >
                    Clear
                  </button>
                </div>
                {previewData.project_count === 0 ? (
                  <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px] text-muted-foreground">
                    You have no projects yet.
                  </p>
                ) : (
                  <ProjectPreviewList projects={previewData.projects} />
                )}
              </div>
            )}

            <Button
              className="w-full gap-2"
              onClick={downloadZip}
              disabled={downloading || (previewData && previewData.project_count === 0)}
              data-testid="bulk-export-download-btn"
            >
              {downloading
                ? <><Loader2 className="h-4 w-4 animate-spin" />Building archive…</>
                : <><Download className="h-4 w-4" />Download ZIP</>}
            </Button>
          </section>

          <div className="border-t border-border" />

          {/* ── IMPORT SECTION ─────────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="flex items-center gap-1.5 text-[13px] font-semibold">
              <Upload className="h-3.5 w-3.5 text-primary" />
              Import manuscripts from files
            </h3>
            <p className="text-[11.5px] text-muted-foreground leading-relaxed">
              Upload a ZIP archive (or a single .docx, .txt, or .pdf) and each file becomes a new
              project. Chapter breaks are auto-detected from headings like "Chapter 1", "Chapter One".
            </p>

            {/* Drop zone */}
            <div
              className={`relative flex min-h-[80px] cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-center transition-colors
                ${dragOver ? 'border-primary bg-primary/8' : 'border-border hover:border-primary/50 hover:bg-muted/40'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="bulk-import-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="sr-only"
                onChange={handleFileChange}
                data-testid="bulk-import-file-input"
              />
              {importFile ? (
                <>
                  <BookOpen className="h-6 w-6 text-primary" />
                  <span className="text-[12px] font-medium text-foreground truncate max-w-[280px]">{importFile.name}</span>
                  <span className="text-[10.5px] text-muted-foreground">
                    {(importFile.size / 1024).toFixed(1)} KB · Click to change
                  </span>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <span className="text-[12px] text-muted-foreground">Drop a file here or click to browse</span>
                  <span className="text-[10.5px] text-muted-foreground/70">.zip · .docx · .txt · .pdf</span>
                </>
              )}
            </div>

            <Button
              className="w-full gap-2"
              onClick={runImport}
              disabled={!importFile || importing}
              data-testid="bulk-import-run-btn"
            >
              {importing
                ? <><Loader2 className="h-4 w-4 animate-spin" />Importing…</>
                : <><Upload className="h-4 w-4" />Import manuscripts</>}
            </Button>

            {importResult && <ImportResult result={importResult} />}
          </section>

          {/* ── EXTRACTION REVIEW ────────────────────────────────────────── */}
          {reviewProjectId && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-[13px] font-semibold">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  AI Metadata Extraction
                </h3>
                <button
                  type="button"
                  onClick={() => setReviewProjectId(null)}
                  className="text-[10.5px] text-muted-foreground hover:text-foreground underline"
                >
                  Dismiss
                </button>
              </div>
              <p className="text-[11.5px] text-muted-foreground">
                The AI is reading your manuscript and extracting metadata — review and adjust each field,
                then apply to your project.
              </p>
              <ExtractionReviewPanel
                projectId={reviewProjectId}
                onApplied={() => toast.success('Metadata applied to project')}
              />
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
